import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import SelectField from '../common/SelectField.js';
import { onContractService } from "../../services/onContractService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Alert from "../common/Alert"
import { ArrowLeft, Truck, FileText, Calculator, CheckSquare } from "lucide-react"
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import { useTranslation } from 'react-i18next';


// ─── Defined OUTSIDE the component so it never gets remounted on re-render ───
const ChargeInput = ({ label, name, hint, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-secondary mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
      <input
        type="text"
        inputMode="decimal"
        name={name}
        value={value}
        onChange={onChange}
        placeholder="0.00"
        className="w-full pl-7 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 text-sm"
      />
    </div>
  </div>
)

const AddOnContract = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()

  const [step, setStep] = useState(1) // 1=vehicle, 2=ml-sheets, 3=charges
  const [submitting, setSubmitting] = useState(false)
  const [loadingVehicles, setLoadingVehicles] = useState(true)
  const [loadingMLSheets, setLoadingMLSheets] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)

  const [vehicles, setVehicles] = useState([])
  const [availableMLSheets, setAvailableMLSheets] = useState([])
  const [selectedMLSheetIds, setSelectedMLSheetIds] = useState([])
  const [preview, setPreview] = useState({ total_shipments: 0, total_revenue: 0 })

  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    vehicle_id:   (v) => validators.select(v, 'vehicle'),
    start_date:   (v) => validators.required(v, 'Start date'),
    end_date:     (v) => validators.required(v, 'End date'),
  }

  const [formData, setFormData] = useState({
    vehicle_id: "",
    start_date: "",
    end_date: "",
    contract_value: "",
    handling_charges: "",
    parking_charges: "",
    other_charges: "",
  })

  // Derived trip P&L preview
  const tripPL =
    parseFloat(preview.total_revenue || 0) -
    parseFloat(formData.contract_value || 0) -
    parseFloat(formData.handling_charges || 0) -
    parseFloat(formData.parking_charges || 0) -
    parseFloat(formData.other_charges || 0)

  useEffect(() => {
    fetchVehicles()
  }, [])

  useEffect(() => {
    if (formData.vehicle_id) {
      fetchAvailableMLSheets(formData.vehicle_id)
      setSelectedMLSheetIds([])
      setPreview({ total_shipments: 0, total_revenue: 0 })
    } else {
      setAvailableMLSheets([])
    }
  }, [formData.vehicle_id])

  useEffect(() => {
    if (selectedMLSheetIds.length > 0) {
      fetchPreview(selectedMLSheetIds)
    } else {
      setPreview({ total_shipments: 0, total_revenue: 0 })
    }
  }, [selectedMLSheetIds])

  const fetchVehicles = async () => {
    try {
      setLoadingVehicles(true)
      const res = await onContractService.getAllVehicles()
      const data = res.data.vehicles || res.data.data || res.data || []
      setVehicles(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error fetching vehicles:", err)
      Alert.error("Failed to load vehicles")
    } finally {
      setLoadingVehicles(false)
    }
  }

  const fetchAvailableMLSheets = async (vehicleId) => {
    try {
      setLoadingMLSheets(true)
      const res = await onContractService.getAvailableMLSheets(vehicleId)
      const data = res.data.available_ml_sheets || []
      setAvailableMLSheets(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error fetching ML sheets:", err)
      Alert.error("Failed to load available ML sheets")
      setAvailableMLSheets([])
    } finally {
      setLoadingMLSheets(false)
    }
  }

  const fetchPreview = async (ids) => {
    try {
      setLoadingPreview(true)
      const res = await onContractService.getMLSheetData(ids)
      setPreview({
        total_shipments: res.data.total_shipments || 0,
        total_revenue: res.data.total_revenue || 0,
      })
    } catch (err) {
      console.error("Error fetching preview:", err)
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    let filtered = value
    if (['contract_value', 'handling_charges', 'parking_charges', 'other_charges'].includes(name))
      filtered = inputFilters.amount(value)

    setFormData({ ...formData, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleMLSheetToggle = (mlSheetId) => {
    setSelectedMLSheetIds((prev) =>
      prev.includes(mlSheetId) ? prev.filter((id) => id !== mlSheetId) : [...prev, mlSheetId]
    )
  }

  const handleSelectAll = () => {
    if (selectedMLSheetIds.length === availableMLSheets.length) {
      setSelectedMLSheetIds([])
    } else {
      setSelectedMLSheetIds(availableMLSheets.map((ml) => ml.ml_sheet_id))
    }
  }

  const validateStep1 = () => {
    if (!validate(formData, validationRules)) {
      Alert.error("Please fix the highlighted errors")
      return false
    }
    if (formData.start_date > formData.end_date) {
      Alert.error("End date must be after start date")
      return false
    }
    return true
  }

  const validateStep2 = () => {
    if (selectedMLSheetIds.length === 0) return Alert.error("Please select at least one ML Sheet") || false
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateStep2()) return

    try {
      setSubmitting(true)
      const payload = {
        vehicle_id: parseInt(formData.vehicle_id),
        start_date: formData.start_date,
        end_date: formData.end_date,
        ml_sheet_ids: selectedMLSheetIds,
        contract_value: parseFloat(formData.contract_value || 0),
        handling_charges: parseFloat(formData.handling_charges || 0),
        parking_charges: parseFloat(formData.parking_charges || 0),
        other_charges: parseFloat(formData.other_charges || 0),
      }
      const res = await onContractService.create(payload)
      Alert.success("Contract created successfully")
      navigate(`/on-contracts/view/${res.data.contract.contract_id}`)
    } catch (err) {
      console.error("Error creating contract:", err)
      Alert.error(err.response?.data?.error || "Failed to create contract")
    } finally {
      setSubmitting(false)
    }
  }

  const selectedVehicle = vehicles.find((v) => String(v.id) === String(formData.vehicle_id))

  return (
    <DashboardLayout>

                {/* Page Header */}
                <div className="flex items-center gap-3 mb-6">
                  <button onClick={() => navigate("/on-contracts")} className="text-gray-500 hover:text-gray-700">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="text-xl font-bold text-secondary">Add On-Contract</h1>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center gap-0 mb-6">
                  {[
                    { num: 1, label: "Vehicle & Dates", icon: <Truck className="w-4 h-4" /> },
                    { num: 2, label: "Select ML Sheets", icon: <FileText className="w-4 h-4" /> },
                    { num: 3, label: "Charges & P&L", icon: <Calculator className="w-4 h-4" /> },
                  ].map((s, i) => (
                    <div key={s.num} className="flex items-center flex-1">
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
                            step === s.num
                              ? "bg-blue-500 border-blue-500 text-white"
                              : step > s.num
                              ? "bg-green-500 border-green-500 text-white"
                              : "bg-white border-gray-300 text-gray-400"
                          }`}
                        >
                          {step > s.num ? <CheckSquare className="w-4 h-4" /> : s.icon}
                        </div>
                        <span className={`text-xs mt-1 font-semibold ${step === s.num ? "text-blue-600" : "text-gray-400"}`}>
                          {s.label}
                        </span>
                      </div>
                      {i < 2 && <div className={`h-0.5 flex-1 mx-2 ${step > s.num ? "bg-green-400" : "bg-gray-200"}`} />}
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSubmit}>

                  {/* ── STEP 1: Vehicle & Dates ── */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <div className="bg-white rounded-xl shadow p-5">
                        <h2 className="font-semibold text-secondary mb-4 flex items-center gap-2 border-b pb-2">
                          Vehicle & Contract Period
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                          <div>
                            <label className="block text-sm font-medium text-secondary mb-1">
                              Vehicle <span className="text-red-500">*</span>
                            </label>
                            {loadingVehicles ? (
                              <div className="text-sm text-gray-400 py-2">Loading vehicles...</div>
                            ) : (
                              <>
                                <SelectField variant="form"
                                  name="vehicle_id"
                                  value={formData.vehicle_id}
                                  onChange={handleChange}
                                  required
                                  className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:border-blue-500 text-sm ${errors.vehicle_id ? 'border-red-400' : 'border-gray-300'}`}
                                >
                                  <option value="">Select a vehicle</option>
                                  {vehicles.map((v) => (
                                    <option key={v.id} value={v.id}>
                                      {v.vehicle_number} — {v.vehicle_name}
                                    </option>
                                  ))}
                                </SelectField>
                                <ValidationMessage error={errors.vehicle_id} />
                              </>
                            )}
                            {selectedVehicle && (
                              <p className="text-xs text-green-600 mt-1">
                                Type: {selectedVehicle.vehicle_type} | Status: {selectedVehicle.status}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary mb-1">
                              Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              name="start_date"
                              value={formData.start_date}
                              onChange={handleChange}
                              required
                              className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:border-blue-500 text-sm ${errors.start_date ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            <ValidationMessage error={errors.start_date} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary mb-1">
                              End Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              name="end_date"
                              value={formData.end_date}
                              onChange={handleChange}
                              required
                              className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:border-blue-500 text-sm ${errors.end_date ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            <ValidationMessage error={errors.end_date} />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => validateStep1() && setStep(2)}
                          className="px-6 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold text-sm"
                        >
                          Next: Select ML Sheets
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── STEP 2: ML Sheet Selection ── */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <div className="bg-white rounded-xl shadow p-5">
                        <div className="flex items-center justify-between mb-4 border-b pb-2">
                          <h2 className="font-semibold text-secondary flex items-center gap-2">
                            Available ML Sheets ({availableMLSheets.length})
                            {loadingMLSheets && <span className="text-xs text-blue-500 ml-2">Loading...</span>}
                          </h2>
                          {availableMLSheets.length > 0 && (
                            <button
                              type="button"
                              onClick={handleSelectAll}
                              className="px-3 py-1.5 bg-blue-500 text-white text-xs font-semibold rounded-xl hover:bg-blue-600"
                            >
                              {selectedMLSheetIds.length === availableMLSheets.length ? "Deselect All" : "Select All"}
                            </button>
                          )}
                        </div>

                        {selectedVehicle && (
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm">
                            <span className="font-semibold text-blue-700">Vehicle: </span>
                            <span className="text-blue-600">{selectedVehicle.vehicle_number} — {selectedVehicle.vehicle_name}</span>
                            <span className="ml-4 font-semibold text-blue-700">Period: </span>
                            <span className="text-blue-600">{formData.start_date} → {formData.end_date}</span>
                          </div>
                        )}

                        {loadingMLSheets ? (
                          <div className="text-center py-10">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <p className="text-gray-400 mt-2 text-sm">Loading available ML sheets...</p>
                          </div>
                        ) : availableMLSheets.length === 0 ? (
                          <div className="text-center py-10">
                            <p className="text-gray-500 text-sm">No available ML sheets for this vehicle.</p>
                            <p className="text-gray-400 text-xs mt-1">All ML sheets may already be added to a contract.</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {availableMLSheets.map((ml) => {
                              const isSelected = selectedMLSheetIds.includes(ml.ml_sheet_id)
                              return (
                                <label
                                  key={ml.ml_sheet_id}
                                  className={`flex items-start p-3 border rounded-xl cursor-pointer transition-colors ${
                                    isSelected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleMLSheetToggle(ml.ml_sheet_id)}
                                    className="mt-0.5 w-4 h-4 flex-shrink-0"
                                  />
                                  <div className="ml-3 flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className="font-bold text-blue-600 text-sm">{ml.ml_sheet_id}</span>
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded border font-semibold ${
                                          ml.status === "OPEN" || ml.status === "open"
                                            ? "border-green-400 bg-green-50 text-green-700"
                                            : "border-gray-400 bg-gray-50 text-gray-700"
                                        }`}
                                      >
                                        {ml.status}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {ml.shipment_ids?.length || 0} shipments
                                      {ml.from_locations?.length > 0 && (
                                        <span className="ml-2">
                                          · {ml.from_locations.map((l) => l.location || l.name).join(", ")} → {ml.to_locations?.map((l) => l.location || l.name).join(", ")}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      Vehicle: {ml.vehicle_number} · Created: {ml.created_at?.split("T")[0]}
                                    </div>
                                  </div>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>

                      {selectedMLSheetIds.length > 0 && (
                        <div className="bg-white rounded-xl shadow p-4">
                          <div className="flex flex-wrap gap-4 items-center">
                            <div className="text-sm">
                              <span className="text-gray-500">Selected ML Sheets: </span>
                              <span className="font-bold text-blue-600">{selectedMLSheetIds.length}</span>
                            </div>
                            {loadingPreview ? (
                              <div className="text-xs text-blue-500">Calculating...</div>
                            ) : (
                              <>
                                <div className="text-sm">
                                  <span className="text-gray-500">Total Shipments: </span>
                                  <span className="font-bold">{preview.total_shipments}</span>
                                </div>
                                <div className="text-sm">
                                  <span className="text-gray-500">Freight: </span>
                                  <span className="font-bold text-green-600">
                                    ₹{parseFloat(preview.total_revenue).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 font-semibold text-sm"
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          onClick={() => validateStep2() && setStep(3)}
                          className="px-6 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold text-sm"
                        >
                          Next: Enter Charges →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── STEP 3: Charges & P&L ── */}
                  {step === 3 && (
                    <div className="space-y-4">
                      <div className="bg-white rounded-xl shadow p-5">
                        <h2 className="font-semibold text-secondary mb-4 flex items-center gap-2 border-b pb-2">
                          Contract Charges
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                          <ChargeInput
                            label="Contract Value *"
                            name="contract_value"
                            //hint="Agreed trip payment to transporter"
                            value={formData.contract_value}
                            onChange={handleChange}
                          />
                          <ChargeInput
                            label="Handling Charges"
                            name="handling_charges"
                            value={formData.handling_charges}
                            onChange={handleChange}
                          />
                          <ChargeInput
                            label="Parking Charges"
                            name="parking_charges"
                            value={formData.parking_charges}
                            onChange={handleChange}
                          />
                          <ChargeInput
                            label="Other Charges"
                            name="other_charges"
                            value={formData.other_charges}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      {/* P&L Summary Card */}
                      <div className="bg-white rounded-xl shadow p-5">
                        <h2 className="font-semibold text-secondary mb-4 flex items-center gap-2 border-b pb-2">
                           Trip P&L Summary
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4">
                          {[
                            { label: "Freight", value: preview.total_revenue, color: "text-green-600" },
                            { label: "Contract Value", value: formData.contract_value || 0, color: "text-gray-700" },
                            { label: "Handling Charges", value: formData.handling_charges || 0, color: "text-gray-700" },
                            { label: "Parking Charges", value: formData.parking_charges || 0, color: "text-gray-700" },
                            { label: "Other Charges", value: formData.other_charges || 0, color: "text-gray-700" },
                            { label: "Total Shipments", value: preview.total_shipments, color: "text-blue-600", isCount: true },
                          ].map((item) => (
                            <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                              <div className="text-xs text-gray-500">{item.label}</div>
                              <div className={`font-bold text-sm mt-1 ${item.color}`}>
                                {item.isCount
                                  ? item.value
                                  : `₹${parseFloat(item.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div
                          className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                            tripPL >= 0 ? "bg-green-50 border-green-400" : "bg-red-50 border-red-400"
                          }`}
                        >
                          <span className="font-bold text-base text-secondary">Trip P&L</span>
                          <span className={`font-bold text-xl ${tripPL >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {tripPL >= 0 ? "+" : ""}₹{tripPL.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          P&L = Revenue − Contract Value − Handling − Parking − Other Charges
                        </p>
                      </div>

                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={() => setStep(2)}
                          className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 font-semibold text-sm"
                        >
                          ← Back
                        </button>
                        <button
                          type="submit"
                          disabled={submitting}
                          className="px-6 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold text-sm disabled:opacity-60"
                        >
                          {submitting ? "Creating..." : "Create Contract"}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
    </DashboardLayout>
  )
}

export default AddOnContract